import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface SustainabilityGoal {
  id: string
  name: string
  category: string
  target_value: number
  current_value: number
  unit: string
  target_year: number
  status: string
}

interface EnergySource {
  id: string
  name: string
  source_type: string
  capacity_kw: number
  location: string
  is_renewable: boolean
  efficiency: number
  status: string
}

export default function EnergyPage() {
  const [goals, setGoals] = useState<SustainabilityGoal[]>([])
  const [sources, setSources] = useState<EnergySource[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [goalsRes, srcRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/energy/sustainability-goals'),
      fetch(API_BASE + '/energy/sources'),
      fetch(API_BASE + '/energy/statistics')
    ])
    if (goalsRes.ok) setGoals((await goalsRes.json()).sustainability_goals || [])
    if (srcRes.ok) setSources((await srcRes.json()).energy_sources || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on_track': case 'achieved': case 'active': return 'bg-green-100 text-green-800'
      case 'at_risk': case 'maintenance': return 'bg-yellow-100 text-yellow-800'
      case 'behind': case 'offline': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Energy Management</h1>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Add Goal</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Consumption (30d)</div>
              <div className="text-2xl font-bold">{statistics.total_consumption_kwh?.toLocaleString()} kWh</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Cost (30d)</div>
              <div className="text-2xl font-bold text-blue-600">${statistics.total_cost_30d?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Carbon Footprint</div>
              <div className="text-2xl font-bold text-orange-600">{statistics.carbon_footprint_kg?.toLocaleString()} kg</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Renewable %</div>
              <div className="text-2xl font-bold text-green-600">{statistics.renewable_percentage}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Efficiency Score</div>
              <div className="text-2xl font-bold">{statistics.energy_efficiency_score}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Sustainability Goals</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Goal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {goals.slice(0, 12).map(goal => (
                  <tr key={goal.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{goal.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{goal.category.replace(/_/g, ' ')}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${Math.min((goal.current_value / goal.target_value) * 100, 100)}%` }}></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{goal.current_value}{goal.unit} / {goal.target_value}{goal.unit}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{goal.target_value}{goal.unit}</div>
                      <div className="text-xs text-gray-500">by {goal.target_year}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(goal.status)}`}>{goal.status.replace(/_/g, ' ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Energy Sources</h3>
            <div className="space-y-3">
              {sources.slice(0, 10).map(src => (
                <div key={src.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{src.name}</span>
                    {src.is_renewable && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Renewable</span>}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{src.source_type.replace(/_/g, ' ')}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{src.capacity_kw} kW - {src.efficiency}% eff</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(src.status)}`}>{src.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
